// Client-side image compression using canvas — used before uploading
// before/after work photos so we don't saturate the phone's data plan or
// the object storage backend.
//
// Given a File/Blob, returns a new JPEG Blob whose long edge is at most
// `maxSize` pixels (default 1600) and whose quality is `quality` (default
// 0.8). Falls back to the original file if anything goes wrong.

export async function compressImage(file, { maxSize = 1600, quality = 0.8, mimeType = "image/jpeg" } = {}) {
  if (!file || !file.type || !file.type.startsWith("image/")) return file;
  try {
    const bitmap = await loadBitmap(file);
    const { width, height } = bitmap;
    const long = Math.max(width, height);
    const scale = long > maxSize ? maxSize / long : 1;
    const targetW = Math.round(width * scale);
    const targetH = Math.round(height * scale);

    const canvas = document.createElement("canvas");
    canvas.width = targetW;
    canvas.height = targetH;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(bitmap, 0, 0, targetW, targetH);

    const blob = await new Promise((resolve) =>
      canvas.toBlob(resolve, mimeType, quality)
    );
    if (!blob) return file;
    // Give it a real filename so multipart/form-data preserves extension
    const nameBase = (file.name || "photo").replace(/\.[^.]+$/, "");
    const ext = mimeType === "image/jpeg" ? "jpg" : mimeType === "image/png" ? "png" : "webp";
    return new File([blob], `${nameBase}.${ext}`, { type: mimeType });
  } catch {
    return file;
  }
}

async function loadBitmap(file) {
  if (typeof createImageBitmap === "function") {
    try { return await createImageBitmap(file); } catch { /* fall through */ }
  }
  // Fallback via HTMLImageElement + object URL
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = (e) => { URL.revokeObjectURL(url); reject(e); };
    img.src = url;
  });
}
